import {
  bindPendingCookieOptOutRetentionOwner,
  releasePendingCookieOptOutRetention,
  retainPendingCookieOptOutAcrossReauthentication,
} from 'features/privacy/interest-based-advertising/platform-tracking-consent';

import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

type SignOut = (isGlobalSignOut?: boolean) => Promise<void>;

interface SignOutForReauthenticationParams {
  authenticatedUserId: string | null;
  setIsReAuthenticating: (value: boolean) => void;
  signOut: SignOut;
  source: string;
}

const hasNoCurrentUser = (error: unknown) => /no current user/i.test(toError(error).message);

const REAUTHENTICATION_STORAGE_KEYS = [
  SyncStorageKey.ORDER,
  SyncStorageKey.SERVICE_MODE,
  SyncStorageKey.STORE,
] as const;

const getReauthenticationStorageSnapshot = () =>
  REAUTHENTICATION_STORAGE_KEYS.map(key => ({
    key,
    value: syncStorage.getObject(key),
  }));

const restoreReauthenticationStorageSnapshot = (
  snapshot: ReturnType<typeof getReauthenticationStorageSnapshot>
) => {
  snapshot.forEach(({ key, value }) => {
    if (value) {
      syncStorage.setObject(key, value);
    }
  });
};

export const signOutForReauthentication = async ({
  authenticatedUserId,
  setIsReAuthenticating,
  signOut,
  source,
}: SignOutForReauthenticationParams) => {
  const reauthenticationStorageSnapshot = getReauthenticationStorageSnapshot();
  setIsReAuthenticating(true);
  bindPendingCookieOptOutRetentionOwner(authenticatedUserId);
  retainPendingCookieOptOutAcrossReauthentication();

  try {
    await signOut(true);
    restoreReauthenticationStorageSnapshot(reauthenticationStorageSnapshot);
  } catch (error) {
    if (!hasNoCurrentUser(error)) {
      throw error;
    }

    restoreReauthenticationStorageSnapshot(reauthenticationStorageSnapshot);

    logger.warn({
      error,
      message: 'No active Cognito user to sign out before re-authentication',
      source,
    });
  } finally {
    releasePendingCookieOptOutRetention();
  }
};
